/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	IEntityInstanceDto
} from '@api/interfaces/entities/entity-instance.dto.interface';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	SharedTypeConstants
} from '@shared/constants/shared-type-constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IEntityNavigationDefinition
} from '@shared/interfaces/application-objects/entity-navigation-definition.interface';
import {
	IEntityNavigationRequest
} from '@shared/interfaces/application-objects/entity-navigation-request.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	NavigationService
} from '@shared/services/navigation.service';

/* eslint-enable max-len */

	/**
	 * Interface for the form layout controls used
	 * in the create transaction wizard.
	 * Provides named access to form fields instead
	 * of using array indices.
	 *
	 * @interface ITransactionFormLayout
	 */
	interface ITransactionFormLayout
	{
		isExistingControl: FormlyFieldConfig;
		typeControl: FormlyFieldConfig;
		legalNameControl: FormlyFieldConfig;
		firstNameControl: FormlyFieldConfig;
		lastNameControl: FormlyFieldConfig;
		searchControl: FormlyFieldConfig;
		accountTableControl: FormlyFieldConfig;
		accountControl: FormlyFieldConfig;
		selectedItemControl: FormlyFieldConfig;
	}

@Component({
	selector: 'create-transaction',
	templateUrl: './create-transaction.component.html',
	styleUrls: []
})

/**
 * A component representing a wizard step for creating a policy transaction.
 *
 * @export
 * @class CreateTransactionComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class CreateTransactionComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the create transaction component.
	 *
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {InsuranceService} insuranceService
	 * The insurance service used to lookup insurance modules.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {NavigationService} navigationService
	 * The navigation service used for centralized entity navigation.
	 * @memberof CreateTransactionComponent
	 */
	public constructor(
		public activityService: ActivityService,
		public insuranceService: InsuranceService,
		public entityInstanceApiService: EntityInstanceApiService,
		public navigationService: NavigationService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof CreateTransactionComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof CreateTransactionComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the mapped form layout controls for named access.
	 *
	 * @private
	 * @type {ITransactionFormLayout}
	 * @memberof CreateTransactionComponent
	 */
	public layout: ITransactionFormLayout;

	/**
	 * Gets or sets a string representing the parent type group.
	 *
	 * @type {string}
	 * @memberof CreateTransactionComponent
	 */
	public parentTypeGroup: string;

	/**
	 * Gets or sets a number representing the parent id.
	 *
	 * @type {number}
	 * @memberof CreateTransactionComponent
	 */
	public parentId: number;

	/**
	 * Gets or sets the product name associated to this transaction.
	 *
	 * @type {string}
	 * @memberof CreateTransactionComponent
	 */
	public productName: string;

	/**
	 * Gets or sets the transaction entity group associated to the current data.
	 *
	 * @type {string}
	 * @memberof CreateTransactionComponent
	 */
	public transactionEntityGroup: string;

	/**
	 * Gets or sets the transaction entity name associated to the current data.
	 *
	 * @type {string}
	 * @memberof CreateTransactionComponent
	 */
	public transactionEntityName: string;

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof CreateTransactionComponent
	 */
	public currentData: any;

	/**
	 * Gets or sets the transaction id.
	 *
	 * @type {number}
	 * @memberof CreateTransactionComponent
	 */
	public transactionId: number;

	/**
	 * Gets or sets the product account settings object.
	 *
	 * @type {any}
	 * @memberof CreateTransactionComponent
	 */
	public accountObject: any;

	/**
	 * Implements the on initialization interface.
	 *
	 * @memberof CreateTransactionComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		this.currentData =
			this.context.source.activeMenuItem.currentData;

		this.setParentEntityInformation();

		this.context.source
			.addToNext(
				this.create.bind(this));
		this.context.source
			.addToValidator(
				this.isWizardStepValid.bind(this));

		if (AnyHelper.isNull(
			this.currentData.data?.accountObject))
		{
			const product: IEntityInstance =
				await this.insuranceService.getProductByName(
					this.currentData.data.productName);
			this.accountObject =
				product.data?.account;
		}
		else
		{
			this.accountObject =
				this.currentData.data?.accountObject;
		}

		await this.performPostInitActions();

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Validates the wizard step data.
	 *
	 * @return {boolean}
	 * True if the step is valid, false otherwise.
	 * @memberof ProductSelectComponent
	 */
	public isWizardStepValid(): boolean
	{
		if (AnyHelper.isNullOrEmpty(
			this.dynamicFormlyLayout))
		{
			return false;
		}

		const isExistingValid: boolean =
			!AnyHelper.isNullOrWhitespace(
				this.currentData.data.isExisting);
		const typeValid: boolean =
			!AnyHelper.isNullOrWhitespace(
				this.currentData.data.type);
		const namesValid: boolean =
			(!AnyHelper.isNullOrWhitespace(
				this.currentData.data.firstName)
				&& !AnyHelper.isNullOrWhitespace(
					this.currentData.data.lastName))
					|| !AnyHelper.isNullOrWhitespace(
						this.currentData.data.legalName);

		return isExistingValid
			&& typeValid
			&& namesValid;
	}

	/**
	 * Handles the validity changed event sent from the child dynamic
	 * formly component. This will update the validity of the form for
	 * action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof CreateTransactionComponent
	 */
	public async validityChanged(
		isValid: boolean): Promise<void>
	{
		this.context.source.validStepChanged(isValid);
	}

	/**
	 * This will send the transaction creation event and navigate to the new
	 * entity.
	 *
	 * @async
	 * @memberof CreateTransactionComponent
	 */
	public async create(): Promise<void>
	{
		this.productName =
			this.currentData.data.productName;

		this.transactionEntityGroup =
			`PolicyTermTransaction.${this.productName}`;

		this.transactionEntityName =
			`PolicyTermTransaction.${this.productName}`;

		const applicantType =
			AnyHelper.isNullOrWhitespace(
				this.currentData.data.type)
				? SharedTypeConstants.nameTypes.individual
				: this.currentData.data.type;

		const transactionData: IEntityInstance =
			this.createTransactionData(
				this.transactionEntityName,
				applicantType,
				this.currentData.data?.legalName,
				this.currentData.data?.legalStructure,
				this.currentData.data?.prefix,
				this.currentData.data?.firstName,
				this.currentData.data?.middleName,
				this.currentData.data?.lastName,
				this.currentData.data?.suffix,
				this.currentData.data?.effectiveDate,
				this.currentData.data?.termLength);

		this.transactionId =
			await this.createEntity(
				transactionData);

		await this.navigateToQuickQuote(
			this.transactionId,
			this.transactionEntityGroup);
	}

	/**
	 * Creates the Transaction entity instance data based on the collected
	 * data. This data will be saved on the creation process.
	 *
	 * @param {string} entityType
	 * The entity type to be created.
	 * @param {string} applicantType
	 *  The applicant type (Individual/Organization).
	 * @param {string} legalName
	 * The organization legal name.
	 * @param {string} legalStructure
	 *  The organization legal structure:
	 * (Corporation/Limited Liability Company/Sole Proprietorship/Non-Profit).
	 * @param {string} prefix
	 * The prefix.
	 * @param {string} firstName
	 *  The first name.
	 * @param {string} middleName
	 * The middle name.
	 * @param {string} lastName
	 *  The last name.
	 * @param {string} suffix
	 * The suffix.
	 * @param {string} effectiveDate
	 * The effective date.
	 * @param {string} termLength
	 * The term length.
	 * @memberof CreateTransactionComponent
	 */
	private createTransactionData(
		entityType: string,
		applicantType: string,
		legalName: string,
		legalStructure: string,
		prefix: string,
		firstName: string,
		middleName: string,
		lastName: string,
		suffix: string,
		effectiveDate: string,
		termLength: string): IEntityInstance
	{
		const isIndividualApplicant: boolean =
			applicantType === SharedTypeConstants.nameTypes.individual;

		const isOrganizationType: boolean =
			applicantType === SharedTypeConstants.nameTypes.organization;

		const transactionData: IEntityInstance =
			<IEntityInstance>
			{
				id: 0,
				entityType: entityType,
				versionNumber: null,
				data: {
					effectiveDate: effectiveDate,
					interests: [
						{
							type: 'NamedInsured',
							subType: 'Primary',
							characteristics: {
								name: {
									type: applicantType,
									legalName: isOrganizationType
										? legalName ?? null
										: null,
									legalStructure: isOrganizationType
										? legalStructure ?? null
										: null,
									prefix: isIndividualApplicant
										? prefix ?? null
										: null,
									firstName: isIndividualApplicant
										? firstName ?? null
										: null,
									middleName: isIndividualApplicant
										? middleName ?? null
										: null,
									lastName: isIndividualApplicant
										? lastName ?? null
										: null,
									suffix: isIndividualApplicant
										? suffix ?? null
										: null
								}
							}
						}
					],
					termLength: Number.parseInt(
						termLength,
						AppConstants.parseRadix)
				}
			};

		return transactionData;
	}

	/**
	 * This will send the entity creation event.
	 *
	 * @param {IEntityInstance} entityInstanceData
	 *  The entity instance data to be saved.
	 * @async
	 * @memberof CreateTransactionComponent
	 */
	private async createEntity(
		entityInstanceData: IEntityInstance): Promise<number>
	{
		setTimeout(
			() =>
			{
				this.context.source.wizardStepLoading = true;
			});

		const displayName: string =
			this.productName;

		const newEntityId: number =
			await this.activityService.handleActivity<number>(
				new Activity<number>(
					this.createEntityInstance(
						entityInstanceData),
					'<strong>Creating transaction</strong>',
					'<strong>Transaction created</strong>',
					`${displayName} has been created.`,
					`${displayName} has not been created.`));

		return newEntityId;
	}

	/**
	 * Creates an entity instance and all entity relationships.
	 *
	 * @param {IEntityInstance} entityInstanceData
	 *  The entity instance data to be saved.
	 * @return {Promise<number>}
	 * The id of the newly created entity.
	 * @memberof CreateTransactionComponent
	 */
	private async createEntityInstance(
		entityInstanceData: IEntityInstance): Promise<number>
	{
		this.entityInstanceApiService
			.entityInstanceTypeGroup =
				this.transactionEntityGroup;

		const createdEntityId: number =
			await this.entityInstanceApiService
				.createEntityInstance(
					entityInstanceData,
					this.parentTypeGroup,
					this.parentId,
					{
						productName: this.productName,
						accountNumber: this.currentData.data.accountNumber
					});

		return createdEntityId;
	}

	/**
	 * This will navigate to the quick quote using the entity id provided.
	 *
	 * @param {number} entityId
	 * The entity id to navigate.
	 * @param {string} group
	 *  The entity group associated to the navigation.
	 * @async
	 * @memberof CreateTransactionComponent
	 */
	private async navigateToQuickQuote(
		entityId: number,
		group: string)
	{
		const navigationDefinition: IEntityNavigationDefinition =
			await this.navigationService
				.getEntityNavigationDefinition(
					<IEntityNavigationRequest>
					{
						id: entityId,
						entityType: this.transactionEntityName,
						entityTypeGroup: group
					});

		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		this.context.source.storeData(
			this.currentData);

		await this.navigationService.navigateToEntityDefinition(
			navigationDefinition);
	}

	/**
	 * Sets the parent entity information.
	 *
	 * @memberof CreateTransactionComponent
	 */
	private setParentEntityInformation()
	{
		this.parentTypeGroup =
			'Organization.Agencies';
		this.parentId =
			this.currentData.data.agencyId;
	}

	/**
	 * Handles the post initialization actions.
	 * Creates the dynamic formly layout for the wizard step.
	 *
	 * @return {Promise<void>}
	 * @async
	 * @private
	 * @memberof CreateTransactionComponent
	 */
	private async performPostInitActions(): Promise<void>
	{
		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				{
					key: 'data.isExisting',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Existing Account Holder?',
						description: 'Determines if an account exists.',
						placeholder: AppConstants.empty,
						showClear: true,
						required: true,
						dataPromise: this.getIsExistingOptions.bind(this),
						labelTemplate: '${item.label}',
						valueTemplate: '${item.value}',
						change: this.onIsExistingChange.bind(this)
					}
				},
				{
					key: 'data.type',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Account Type',
						description: 'The account type.',
						placeholder: AppConstants.empty,
						required: true,
						options:
							this.accountObject
								?.enabledAccountTypes
								?.map(
									(type: string) =>
										({
											value: type,
											label: type
										}))
											?? [],
						change: this.onTypeChange.bind(this)
					},
					expressions: {
						hide: 'true'
					}
				},
				{
					key: 'data.legalName',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Legal Name',
						description: 'The account legal name.',
						placeholder: AppConstants.empty,
						required: true
					},
					expressions: {
						hide: 'true'
					}
				},
				{
					key: 'data.firstName',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'First Name',
						description: 'The account first name.',
						placeholder: AppConstants.empty,
						required: true
					},
					expressions: {
						hide: 'true'
					}
				},
				{
					key: 'data.lastName',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Last Name',
						description: 'The account last name.',
						placeholder: AppConstants.empty,
						required: true
					},
					expressions: {
						hide: 'true'
					}
				},
				{
					key: 'data.search',
					type: FormlyConstants.customControls.customInputSearch,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Account Holder Search',
						change: this.onSearchChange.bind(this),
						placeholder: 'Filter',
						searchTooltip: '<div>Search terms entered here '
							+ 'will filter down to only '
							+ 'the items that hold all of the '
							+ 'search terms.<ul><li>In order '
							+ 'to search for dates, please use the '
							+'following format \'YYYY-MM-DD\'.'
							+ '</li><li>In order to search for numerical '
							+ 'values, please enter '
							+ 'the number without commas or format '
							+ 'characters.</li></ul></div>',
						gridColumns: 12
					},
					expressions: {
						hide: 'true'
					}
				},
				{
					key: 'data.accounts',
					type: FormlyConstants.customControls.customTableDisplay,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						loadingTableDefinitions: false,
						fieldContext: this,
						tableDefinitions: {
							tableTitle: 'Account',
							dataKey: 'id',
							hideSettings: true,
							objectSearch: {
								filter: AppConstants.empty,
								orderBy: AppConstants.empty,
								offset: 0,
								limit: 100,
								virtualIndex: 0,
								virtualPageSize: 5
							},
							itemSelection: {
								selectionMode: 'single',
								capturedSelectedItemEventPromise:
									this.onAccountSelected.bind(this)
							},
							availableColumns: [
								{
									dataKey: 'data.name.firstName',
									columnHeader: 'First Name',
									displayOrder: 1
								},
								{
									dataKey: 'data.name.lastName',
									columnHeader: 'Last Name',
									displayOrder: 2
								},
								{
									dataKey: 'data.accountNumber',
									columnHeader: 'Account Number',
									displayOrder: 3
								},
								{
									dataKey: 'data.name.legalName',
									columnHeader: 'Legal Name',
									displayOrder: 4
								}
							],
							apiPromise: this.getAccountsApiPromise.bind(this)
						},
						gridColumns: 12
					},
					expressions: {
						hide: 'true'
					}
				},
				{
					key: 'data.accountNumber',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Account Number',
						description: 'The account number.',
						placeholder: AppConstants.empty
					},
					expressions: {
						hide: 'true'
					}
				},
				{
					key: 'data.selectedItem',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Selected Table Item',
						description: 'the selected account.',
						placeholder: AppConstants.empty
					},
					expressions: {
						hide: 'true'
					}
				}
			];

		this.layout =
			<ITransactionFormLayout>
			{
				isExistingControl: this.dynamicFormlyLayout[0],
				typeControl: this.dynamicFormlyLayout[1],
				legalNameControl: this.dynamicFormlyLayout[2],
				firstNameControl: this.dynamicFormlyLayout[3],
				lastNameControl: this.dynamicFormlyLayout[4],
				searchControl: this.dynamicFormlyLayout[5],
				accountTableControl: this.dynamicFormlyLayout[6],
				accountControl: this.dynamicFormlyLayout[7],
				selectedItemControl: this.dynamicFormlyLayout[8]
			};
	}

	/**
	 * Gets the available options for the existing account holder dropdown.
	 * Pre-populates fields if an account number exists in the initial data.
	 *
	 * @return {Promise<any[]>}
	 * A promise that resolves to an array of options (Yes/No).
	 * @async
	 * @private
	 * @memberof CreateTransactionComponent
	 */
	private async getIsExistingOptions(): Promise<any[]>
	{
		const account: any =
			this.context.source.initialData?.data;
		let options: any[] = [];

		if (!AnyHelper.isNullOrEmpty(account.accountNumber))
		{
			this.layout.accountControl.formControl
				.setValue(account.accountNumber);
			options =
				[
					{
						 value: true,
						 label: 'Yes'
					}
				];
			this.layout.isExistingControl.props.disabled = true;
			this.layout.typeControl.hide = false;
			this.layout.typeControl.formControl
				.setValue(account.type);
			this.layout.typeControl.formControl
				.updateValueAndValidity();
			this.layout.typeControl.props.disabled = true;

			if (!AnyHelper.isNullOrEmpty(account.legalName))
			{
				this.layout.legalNameControl.hide = false;
				this.layout.legalNameControl.formControl
					.setValue(account.legalName);
				this.layout.legalNameControl.formControl
					.updateValueAndValidity();
				this.layout.legalNameControl.props.disabled = true;
			}
			else
			{
				this.layout.firstNameControl.hide = false;
				this.layout.firstNameControl.formControl
					.setValue(account.firstName);
				this.layout.firstNameControl.formControl
					.updateValueAndValidity();
				this.layout.firstNameControl.props.disabled = true;
				this.layout.lastNameControl.hide = false;
				this.layout.lastNameControl.formControl
					.setValue(account.lastName);
				this.layout.lastNameControl.formControl
					.updateValueAndValidity();
				this.layout.lastNameControl.props.disabled = true;
			}
		}
		else
		{
			options =
				[
					{
						value: true,
						label: 'Yes'
					},
					{
						value: false,
						label: 'No'
					}
				];

			this.layout.accountControl.formControl
				.setValue(null);
			this.layout.isExistingControl.props.placeholder =
				'Select an Option';
			this.layout.isExistingControl.formControl
				.setValue('false');
		}

		this.layout.accountControl.formControl
			.updateValueAndValidity();
		this.layout.isExistingControl.formControl
			.updateValueAndValidity();

		return options;
	}

	/**
	 * Handles the change event for the existing account holder field.
	 *
	 * @return {Promise<void>}
	 * @async
	 * @private
	 * @memberof CreateTransactionComponent
	 */
	private async onIsExistingChange(): Promise<void>
	{
		if (!AnyHelper.isNullOrEmpty(
			this.layout.accountControl.formControl.value)
			&& AnyHelper.isNullOrEmpty(
				this.layout.selectedItemControl.formControl.value))
		{
			return;
		}
		else
		{
			this.layout.accountControl.formControl
				.setValue(null);
			this.layout.accountControl.formControl
				.updateValueAndValidity();
		}

		this.currentData.data.type = null;
		this.layout.typeControl.formControl
			.setValue(null);
		this.layout.typeControl.formControl
			.updateValueAndValidity();
		this.layout.typeControl.props.disabled = true;
		this.layout.typeControl.hide = true;

		if (this.layout.isExistingControl.formControl.value === 'true')
		{
			this.layout.searchControl.hide = false;
			this.layout.searchControl.props.disabled = false;
			this.layout.searchControl.props
				.change(this.layout.searchControl);
			this.layout.accountTableControl.hide = false;
			setTimeout(
				() =>
				{
					this.layout.searchControl.focus = true;
				},
				AppConstants.time.fiftyMilliseconds);
		}
		else if (this.layout.isExistingControl.formControl.value === 'false')
		{
			this.layout.searchControl.props.disabled = true;
			this.layout.accountTableControl.props.disabled = true;
			this.layout.searchControl.hide = true;
			this.layout.accountTableControl.hide = true;
			this.layout.typeControl.hide = false;
			this.layout.typeControl.props.disabled = false;
			if (!AnyHelper.isNullOrEmpty(
				this.accountObject?.defaultAccountType))
			{
				this.layout.typeControl.formControl
					.setValue(
						this.accountObject.defaultAccountType);
	   	 	}
			this.layout.typeControl.formControl
				.updateValueAndValidity();
		}
		else
		{
			this.layout.accountTableControl.props.disabled = true;
			this.layout.searchControl.props.disabled = true;
			this.layout.searchControl.hide = true;
			this.layout.accountTableControl.hide = true;
		}

		this.layout.selectedItemControl.formControl
			.setValue(null);
		this.layout.selectedItemControl.formControl
			.updateValueAndValidity();

		this.layout.typeControl.props
			.change(this.layout.typeControl);
		this.context.source
			.validStepChanged(
				this.isWizardStepValid());
	}

	/**
	 * Handles the change event for the account type field.
	 *
	 * @return {Promise<void>}
	 * @async
	 * @private
	 * @memberof CreateTransactionComponent
	 */
	private async onTypeChange(): Promise<void>
	{
		if (!AnyHelper.isNullOrEmpty(
			this.layout.accountControl.formControl.value))
		{
			return;
		}

		if (this.layout.typeControl.formControl.value
			=== SharedTypeConstants.nameTypes.individual)
		{
			this.layout.legalNameControl.props.disabled = true;
			this.layout.legalNameControl.hide = true;
			this.layout.firstNameControl.props.disabled = false;
			this.layout.firstNameControl.hide = false;
			this.layout.lastNameControl.props.disabled = false;
			this.layout.lastNameControl.hide = false;
			setTimeout(
				() =>
				{
					this.layout.firstNameControl.focus = true;
				},
				AppConstants.time.fiftyMilliseconds);
		}
		else if (this.layout.typeControl.formControl.value
			=== SharedTypeConstants.nameTypes.organization)
		{
			this.layout.legalNameControl.props.disabled = false;
			this.layout.legalNameControl.hide = false;
			this.layout.firstNameControl.props.disabled = true;
			this.layout.firstNameControl.hide = true;
			this.layout.lastNameControl.props.disabled = true;
			this.layout.lastNameControl.hide = true;
			setTimeout(
				() =>
				{
		   			this.layout.legalNameControl.focus = true;
				},
				AppConstants.time.fiftyMilliseconds);
		}
		else
		{
			this.layout.legalNameControl.props.disabled = true;
			this.layout.legalNameControl.hide = true;
			this.layout.firstNameControl.props.disabled = true;
			this.layout.firstNameControl.hide = true;
			this.layout.lastNameControl.props.disabled = true;
			this.layout.lastNameControl.hide = true;
		}

		this.layout.legalNameControl.formControl
			.setValue(null);
		this.layout.firstNameControl.formControl
			.setValue(null);
		this.layout.lastNameControl.formControl
			.setValue(null);
		this.layout.legalNameControl.formControl
			.updateValueAndValidity();
		this.layout.firstNameControl.formControl
			.updateValueAndValidity();
		this.layout.lastNameControl.formControl
			.updateValueAndValidity();

	}

	/**
	 * Handles the change event for the account search field.
	 * Triggers table reload when search criteria changes.
	 *
	 * @return {Promise<void>}
	 * @async
	 * @private
	 * @memberof CreateTransactionComponent
	 */
	private async onSearchChange(): Promise<void>
	{
		const searchFilter: string =
			this.layout.searchControl.formControl.value;

		if (!AnyHelper.isNullOrEmpty(searchFilter))
		{
			this.layout.accountTableControl.props
				.loadingTableDefinitions = true;
			setTimeout(
				() =>
				{
					this.layout.accountTableControl.props
						.loadingTableDefinitions = false;
				},
				AppConstants.time.twentyFiveMilliseconds);
		}
		else
		{
			this.layout.accountTableControl.props
				.loadingTableDefinitions = true;
			setTimeout(
				() =>
				{
					this.currentData.data.accounts = [];
					this.layout.accountTableControl.props
						.loadingTableDefinitions = false;
				},
				AppConstants.time.twentyFiveMilliseconds);
		}
	}

	/**
	 * Handles the account selection event from the accounts table.
	 * Populates form fields with the selected account's data.
	 *
	 * @return {Promise<void>}
	 * @async
	 * @private
	 * @memberof CreateTransactionComponent
	 */
	private async onAccountSelected(): Promise<void>
	{
		const commonTableContext: any =
			this.layout.accountTableControl.props.tableDefinitions
				.getCommonTableContext();
		const selectedItem: any =
			commonTableContext.source.selectedItem;

		if (selectedItem == null)
		{
			this.layout.typeControl.formControl
				.setValue(null);
			this.layout.legalNameControl.formControl
				.setValue(null);
			this.layout.firstNameControl.formControl
				.setValue(null);
			this.layout.lastNameControl.formControl
				.setValue(null);
			this.layout.accountControl.formControl
				.setValue(null);
			this.layout.selectedItemControl.formControl
				.setValue(null);

			this.currentData.data.type = null;
			this.currentData.data.legalName = null;
			this.currentData.data.firstName = null;
			this.currentData.data.lastName = null;
			this.currentData.data.accountNumber = null;
		}
		else
		{
			this.layout.typeControl.formControl
				.setValue(selectedItem.data.name.type);
			this.layout.legalNameControl.formControl
				.setValue(selectedItem.data.name.legalName);
			this.layout.firstNameControl.formControl
				.setValue(selectedItem.data.name.firstName);
			this.layout.lastNameControl.formControl
				.setValue(selectedItem.data.name.lastName);
			this.layout.accountControl.formControl
				.setValue(selectedItem.data.accountNumber);
			this.layout.selectedItemControl.formControl
				.setValue(selectedItem);

			// Update data immediately for validator
			this.currentData.data.type =
				selectedItem.data.name.type;
			this.currentData.data.legalName =
				selectedItem.data.name.legalName;
			this.currentData.data.firstName =
				selectedItem.data.name.firstName;
			this.currentData.data.lastName =
				selectedItem.data.name.lastName;
			this.currentData.data.accountNumber =
				selectedItem.data.accountNumber;
		}

		this.layout.typeControl.formControl
			.updateValueAndValidity();
		this.layout.legalNameControl.formControl
			.updateValueAndValidity();
		this.layout.firstNameControl.formControl
			.updateValueAndValidity();
		this.layout.lastNameControl.formControl
			.updateValueAndValidity();
		this.layout.accountControl.formControl
			.updateValueAndValidity();
		this.layout.selectedItemControl.formControl
			.updateValueAndValidity();

		this.context.source
			.validStepChanged(
				this.isWizardStepValid());
	}

	/**
	 * Gets the accounts data for the accounts table.
	 * Queries accounts based on the search filter.
	 *
	 * @return {Promise<any[]>}
	 * A promise that resolves to an array of account entities.
	 * @async
	 * @private
	 * @memberof CreateTransactionComponent
	 */
	private async getAccountsApiPromise(): Promise<any[]>
	{
		const objectSearch: any =
			this.layout.accountTableControl.props.tableDefinitions.objectSearch;

		if (AnyHelper.isNullOrEmpty(
			this.currentData.data.search))
		{
			return [];
		}

		this.entityInstanceApiService.entityInstanceTypeGroup =
			'Accounts';
		const queryResults: IEntityInstanceDto[] =
			await this.entityInstanceApiService.query(
				`keywords.Contains('${this.currentData.data.search}')`,
				'Id desc',
				objectSearch.offset,
				objectSearch.limit);

		return queryResults;
	}
}